import * as React from "react";
import {
  Archive,
  CalendarDays,
  CalendarX2,
  ChevronDown,
  Code2,
  Copy,
  ExternalLink,
  History,
  LayoutGrid,
  List,
  Lock,
  Pencil,
  Power,
  Trash2,
  Users,
} from "lucide-react";

import { BookingPageView, RollingLinkNoticeKind, RollingLinkView, ConferenceProvider, Poll } from "@/api/types";
import { cn } from "@/lib/utils";

import { EmbedSnippetPanel } from "./EmbedSnippetPanel";

import { BookingRows } from "./BookingRows";
import {
  AuditLogUpsell,
  bookingEventToEntry,
  rollingLinkEventToEntry,
  EventTrail,
  pollEventToEntry,
} from "./EventTrail";
import { SortKey, sortLinks } from "./linkSort";
import { activityKey, LinkActivity, LinkKind } from "./useLinkActivity";
import { conferenceProviderLabel } from "@/lib/calendarProviders";

/**
 * One place for ALL the host's booking links — ad-hoc pages and rolling appointments together.
 * Filter by type/status, switch card|list, and click any link to expand the link: its config,
 * who booked it, and its event journal, in three tabs. Rolling links are editable (an Edit button
 * opens the builder); ad-hoc links are fixed once created (a Slots button opens their detail view).
 */

type Kind = LinkKind;

/**
 * How many rows a drawer shows before offering more.
 *
 * <p>This used to be a purely cosmetic cap: every row had already been fetched and "Show all"
 * only stopped hiding them. The lists are paged on the server now, so the button fetches.</p>
 */
const ROWS_BEFORE_CAP = 20;

/** The drawer's action buttons all share one outline treatment. */
const actionCls =
  "inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--foreground)_/_0.28)] bg-card px-3 py-1.5 text-xs font-bold text-foreground transition hover:border-brand-blue";
type TypeFilter = "all" | Kind;
type StatusFilter = "all" | "live" | "past";
type ViewMode = "cards" | "list";

interface LinkItem {
  id: string;
  kind: Kind;
  title: string;
  path: string;
  live: boolean;
  /** Switched off by the host. Drives the Enable/Disable label. */
  disabled: boolean;
  /** Ad-hoc only: run out or expired, so the next teardown step is Archive, not Expire. */
  ended: boolean;
  bookingCount: number;
  createdAt: string;
  /** Soonest booking still ahead, or null when nothing is coming up. */
  nextBookingAt: string | null;
  editable: boolean;
  config: { label: string; value: string }[];
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
function minsText(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${h} hr ${mm} min`;
  if (h) return `${h} hr`;
  return `${mm} min`;
}
/** The video provider, else the physical place, else nothing set. */
function providerLabel(p: ConferenceProvider | null, location: string | null): string {
  if (p) return conferenceProviderLabel(p);
  if (location && location.trim()) return location.trim();
  return "—";
}
function rollingLinkNotice(kind: RollingLinkNoticeKind, minutes: number): string {
  switch (kind) {
    case "END_OF_DAY":
      return "until end of day";
    case "END_OF_NEXT_DAY":
      return "until end of next day";
    case "END_OF_WEEK":
      return "until end of week";
    default:
      return minutes > 0 ? minsText(minutes) : "no minimum";
  }
}

function adhocToItem(p: BookingPageView): LinkItem {
  const ended = p.ends_at ? new Date(p.ends_at).getTime() <= Date.now() : false;
  return {
    id: p.id,
    kind: "adhoc",
    title: p.title,
    path: p.url_path,
    live: p.status === "ACTIVE" && !ended,
    disabled: p.status === "DISABLED",
    ended: ended || p.status === "EXPIRED",
    bookingCount: p.booking_count,
    createdAt: p.created_at,
    nextBookingAt: p.next_booking_at,
    editable: false,
    config: [
      { label: "Duration", value: `${p.slot_minutes} min` },
      { label: "Availability", value: `${p.windows.length} time block${p.windows.length === 1 ? "" : "s"}` },
      { label: "Live until", value: fmtDate(p.ends_at ?? p.valid_until) },
      { label: "Min notice", value: p.min_notice_minutes > 0 ? minsText(p.min_notice_minutes) : "none" },
      { label: "Max bookings", value: p.max_bookings != null ? String(p.max_bookings) : "—" },
      { label: "One per person", value: p.one_per_invitee ? "Yes" : "No" },
      { label: "Cancel link", value: p.allow_cancel ? "Yes" : "No" },
      { label: "Location", value: providerLabel(p.conferencing_provider, p.location) },
    ],
  };
}

function rollingLinkToItem(c: RollingLinkView): LinkItem {
  const ended = c.valid_until ? new Date(c.valid_until).getTime() <= Date.now() : false;
  const gap = Math.max(0, c.interval_minutes - c.slot_minutes);
  return {
    id: c.id,
    kind: "rolling",
    title: c.title,
    path: `/${c.public_path}`,
    live: c.status === "ACTIVE" && !ended,
    disabled: c.status === "DISABLED",
    ended: false, // a rolling link is disabled or removed, never expired
    bookingCount: c.booking_count,
    createdAt: c.created_at,
    nextBookingAt: c.next_booking_at,
    editable: true,
    config: [
      { label: "Duration", value: `${c.slot_minutes} min` },
      { label: "Interval", value: gap > 0 ? `${gap} min gap` : "back-to-back" },
      { label: "Availability", value: `${c.availability.length} weekly band${c.availability.length === 1 ? "" : "s"}` },
      { label: "Min notice", value: rollingLinkNotice(c.notice_kind, c.min_notice_minutes) },
      { label: "Bookable until", value: c.valid_until ? fmtDate(c.valid_until) : "no end date" },
      { label: "Location", value: providerLabel(c.conferencing_provider, c.location) },
    ],
  };
}

/**
 * A poll as a link row. Its "bookings" are responses, and its outcome is one confirmed time rather
 * than a run of them, so the config rows say so instead of borrowing the other two's words.
 */
function pollToItem(p: Poll): LinkItem {
  const outcome =
    p.status === "CONFIRMED"
      ? p.confirmation_sent_at
        ? "confirmed and sent"
        : "confirmed, not yet sent"
      : p.status === "CLOSED"
        ? "voting closed"
        : "collecting answers";
  return {
    id: p.id,
    kind: "poll",
    title: p.title,
    path: `/poll/${p.slug}`,
    live: p.voting_open,
    disabled: p.status === "CLOSED",
    ended: false, // a poll is closed, confirmed or removed — never expired
    bookingCount: p.respondent_count,
    createdAt: p.created_at,
    // A poll's "next" date is the time it settled on, once it has one.
    nextBookingAt:
      p.options.find((o) => o.id === p.confirmed_option_id)?.start_at ?? null,
    editable: p.status !== "CONFIRMED",
    config: [
      { label: "Duration", value: `${p.duration_minutes} min` },
      { label: "Proposed times", value: `${p.options.length}` },
      { label: "Responses", value: `${p.respondent_count}` },
      { label: "Respond by", value: p.respond_by ?? "no deadline" },
      { label: "Vote counts", value: p.show_counts ? "shown after voting" : "hidden" },
      { label: "Location", value: providerLabel(p.conferencing_provider, p.location) },
      { label: "Outcome", value: outcome },
    ],
  };
}

export function AllLinksView({
  pages,
  rollingLinks,
  polls = [],
  loading = false,
  activity,
  onLoadActivity,
  onLoadMoreActivity,
  onEditRollingLink,
  onOpenAdhocDetail,
  onOpenPollDetail,
  onCopy,
  onToggleStatus,
  onExpire,
  onArchive,
  onRemoveRollingLink,
  onRemovePoll,
}: {
  pages: BookingPageView[];
  rollingLinks: RollingLinkView[];
  polls?: Poll[];
  /** The links are still being fetched, so an empty list isn't yet "you have none". */
  loading?: boolean;
  /** Bookings + journal per link, cached by the page so it survives leaving for the builder. */
  activity: Record<string, LinkActivity>;
  onLoadActivity: (kind: Kind, id: string) => void;
  onLoadMoreActivity: (kind: Kind, id: string) => void;
  onEditRollingLink: (rollingLinkId: string) => void;
  onOpenAdhocDetail: (pageId: string) => void;
  onOpenPollDetail?: (pollId: string) => void;
  onCopy: (path: string) => void;
  onToggleStatus: (kind: Kind, id: string) => void;
  onExpire: (pageId: string) => void;
  onArchive: (pageId: string) => void;
  onRemoveRollingLink: (rollingLinkId: string) => void;
  onRemovePoll?: (pollId: string) => void;
}) {
  const [type, setType] = React.useState<TypeFilter>("all");
  const [status, setStatus] = React.useState<StatusFilter>("all");
  const [mode, setMode] = React.useState<ViewMode>("list");
  const [sort, setSort] = React.useState<SortKey>("creation");
  const [openId, setOpenId] = React.useState<string | null>(null);

  const items = React.useMemo(
    () =>
      sortLinks(
        [...pages.map(adhocToItem), ...rollingLinks.map(rollingLinkToItem), ...polls.map(pollToItem)],
        sort,
      ),
    [pages, rollingLinks, polls, sort],
  );

  const shown = items.filter(
    (i) =>
      (type === "all" || i.kind === type) &&
      (status === "all" || (status === "live" ? i.live : !i.live)),
  );

  const counts = {
    all: items.length,
    live: items.filter((i) => i.live).length,
  };

  const seg = (
    value: string,
    active: boolean,
    onClick: () => void,
    label: string,
  ): React.ReactNode => (
    <button
      key={value}
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "px-3 py-1 text-xs font-semibold transition",
        active ? "bg-brand-blue text-white" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="comic-shadow space-y-4 rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-foreground">All bookings</h2>
          <span className="text-sm text-muted-foreground">({counts.all})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border border-[hsl(var(--foreground)_/_0.28)]">
            <button
              type="button"
              onClick={() => setMode("cards")}
              aria-label="Card view"
              className={cn("grid h-8 w-9 place-items-center", mode === "cards" ? "bg-muted text-foreground" : "text-muted-foreground")}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setMode("list")}
              aria-label="List view"
              className={cn("grid h-8 w-9 place-items-center", mode === "list" ? "bg-muted text-foreground" : "text-muted-foreground")}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Type</span>
          <div className="inline-flex overflow-hidden rounded-full border border-[hsl(var(--foreground)_/_0.28)]">
            {seg("all", type === "all", () => setType("all"), "All")}
            {seg("adhoc", type === "adhoc", () => setType("adhoc"), "Ad-hoc")}
            {seg("rolling", type === "rolling", () => setType("rolling"), "Rolling")}
            {seg("poll", type === "poll", () => setType("poll"), "Poll")}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Status</span>
          <div className="inline-flex overflow-hidden rounded-full border border-[hsl(var(--foreground)_/_0.28)]">
            {seg("all", status === "all", () => setStatus("all"), "All")}
            {seg("live", status === "live", () => setStatus("live"), `Live (${counts.live})`)}
            {seg("past", status === "past", () => setStatus("past"), "Past")}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Sort by</span>
          <div className="inline-flex overflow-hidden rounded-full border border-[hsl(var(--foreground)_/_0.28)]">
            {seg("creation", sort === "creation", () => setSort("creation"), "Creation")}
            {seg("upcoming", sort === "upcoming", () => setSort("upcoming"), "Soon upcoming")}
          </div>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Loading…
        </p>
      ) : shown.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing matches these filters.
        </p>
      ) : (
        <div className={cn("grid gap-3", mode === "cards" && "sm:grid-cols-2")}>
          {shown.map((item) => (
            <LinkCard
              key={`${item.kind}-${item.id}`}
              item={item}
              open={openId === `${item.kind}-${item.id}`}
              activity={activity[activityKey(item.kind, item.id)]}
              onLoad={onLoadActivity}
              onLoadMore={onLoadMoreActivity}
              onToggle={() =>
                setOpenId((cur) => (cur === `${item.kind}-${item.id}` ? null : `${item.kind}-${item.id}`))
              }
              onEditRollingLink={onEditRollingLink}
              onOpenAdhocDetail={onOpenAdhocDetail}
              onOpenPollDetail={onOpenPollDetail}
              onCopy={onCopy}
              onToggleStatus={onToggleStatus}
              onExpire={onExpire}
              onArchive={onArchive}
              onRemoveRollingLink={onRemoveRollingLink}
              onRemovePoll={onRemovePoll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type Tab = "details" | "bookings" | "history";

function LinkCard({
  item,
  open,
  activity,
  onLoad,
  onLoadMore,
  onToggle,
  onEditRollingLink,
  onOpenAdhocDetail,
  onOpenPollDetail,
  onCopy,
  onToggleStatus,
  onExpire,
  onArchive,
  onRemoveRollingLink,
  onRemovePoll,
}: {
  item: LinkItem;
  open: boolean;
  activity: LinkActivity | undefined;
  onLoad: (kind: Kind, id: string) => void;
  onLoadMore: (kind: Kind, id: string) => void;
  onToggle: () => void;
  onEditRollingLink: (rollingLinkId: string) => void;
  onOpenAdhocDetail: (pageId: string) => void;
  onOpenPollDetail?: (pollId: string) => void;
  onCopy: (path: string) => void;
  onToggleStatus: (kind: Kind, id: string) => void;
  onExpire: (pageId: string) => void;
  onArchive: (pageId: string) => void;
  onRemoveRollingLink: (rollingLinkId: string) => void;
  onRemovePoll?: (pollId: string) => void;
}) {
  const [tab, setTab] = React.useState<Tab>("details");
  const [showAll, setShowAll] = React.useState(false);
  const [showEmbed, setShowEmbed] = React.useState(false);

  // Bookings and the journal are only worth fetching once you actually open the link.
  React.useEffect(() => {
    if (open) onLoad(item.kind, item.id);
  }, [open, onLoad, item.kind, item.id]);

  const rows = activity?.rows ?? [];
  const capped = showAll ? rows : rows.slice(0, ROWS_BEFORE_CAP);
  // The number in the button counts the rows in hand, not the link's booking_count. The research
  // proposed booking_count on the grounds that the server already sends it, but it counts CONFIRMED
  // rows only (BookingService.confirmedCount, countByRollingLinkIdAndStatus) while this list also
  // shows cancelled ones. A link with 30 confirmed and 5 cancelled would have offered "Show all
  // (30)" and then shown 35. Counting what is in hand is never wrong: the first press reveals
  // exactly the number it promised, and "Load more" takes over from there.
  const total = rows.length;
  // Either there are fetched rows still hidden behind the cap, or the server has more to send.
  const more = rows.length > capped.length || (showAll && (activity?.hasMore ?? false));
  const loadingMore = activity?.loadingMore ?? false;
  const cancelled = rows.filter((r) => r.cancelled).length;
  const journal = activity?.journal;
  // Both event vocabularies collapse to the one trail shape, so the history reads identically
  // whichever kind of link you opened.
  const entries = React.useMemo(() => {
    if (journal == null) return [];
    if (journal.kind === "adhoc") return journal.events.map(bookingEventToEntry);
    if (journal.kind === "poll") return journal.events.map(pollEventToEntry);
    return journal.events.map(rollingLinkEventToEntry);
  }, [journal]);
  const eventCount = entries.length;
  const pending = activity == null || activity.loading;
  // On Free the journal comes back 402. Show the count as locked rather than "(0)", which would
  // read as "nothing happened to this link" — the opposite of the truth.
  const journalLocked = activity?.journalLocked ?? false;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border transition",
        open ? "border-teal-500 sm:col-span-2" : "border-[hsl(var(--foreground)_/_0.18)] hover:border-teal-400",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        {/* Brand tokens rather than raw teal/slate: the palette is the whole point of the reskin,
            and a third kind was the moment two hard-coded hues became three. */}
        <span
          className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
            item.kind === "rolling"
              ? "border-brand-teal/40 bg-brand-teal/10 text-foreground"
              : item.kind === "poll"
                ? "border-brand-yellow/60 bg-brand-yellow/15 text-foreground"
                : "border-border bg-muted text-foreground",
          )}
        >
          {item.kind === "rolling" ? "Rolling" : item.kind === "poll" ? "Poll" : "Ad-hoc"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", item.live ? "bg-green-500" : "bg-gray-400")} />
            <span className="truncate font-semibold text-foreground">{item.title}</span>
          </span>
          <span className="mt-0.5 block truncate font-mono text-[11px] text-muted-foreground">{item.path}</span>
        </span>
        {/* The next date is what "Soon upcoming" orders by, so it has to be visible or the
            reordering looks arbitrary. */}
        {item.nextBookingAt && (
          <span className="shrink-0 text-xs font-semibold text-foreground">
            {fmtDate(item.nextBookingAt)}
          </span>
        )}
        <span className="shrink-0 text-xs text-muted-foreground">
          {item.bookingCount} {item.kind === "poll" ? "answered" : "booked"}
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180 text-teal-600")} />
      </button>

      {open && (
        <div className="border-t border-dashed border-border px-4 pb-4 pt-3">
          <div className="mb-3 inline-flex overflow-hidden rounded-full border border-[hsl(var(--foreground)_/_0.28)]">
            <TabBtn active={tab === "details"} onClick={() => setTab("details")} label="Details" />
            <TabBtn
              active={tab === "bookings"}
              onClick={() => setTab("bookings")}
              icon={<Users className="h-3 w-3" />}
              label={
                item.kind === "poll"
                  ? pending
                    ? "Responses"
                    : `Responses (${rows.length})`
                  : pending
                    ? "Bookings"
                    : `Bookings (${rows.length})`
              }
            />
            <TabBtn
              active={tab === "history"}
              onClick={() => setTab("history")}
              icon={journalLocked ? <Lock className="h-3 w-3" /> : <History className="h-3 w-3" />}
              label={pending || journalLocked ? "History" : `History (${eventCount})`}
            />
          </div>

          {tab === "details" && (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3">
                {item.config.map((c) => (
                  <div key={c.label} className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{c.label}</p>
                    <p className="truncate text-[13px] font-semibold text-foreground">{c.value}</p>
                  </div>
                ))}
              </div>
              {!item.editable && item.kind === "adhoc" && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" /> Ad-hoc links are fixed once created. View only.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {item.kind === "poll" ? (
                  <button
                    type="button"
                    onClick={() => onOpenPollDetail?.(item.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue px-3 py-1.5 text-xs font-bold text-white transition hover:brightness-110"
                  >
                    <Users className="h-3.5 w-3.5" /> Answers
                  </button>
                ) : item.editable ? (
                  <button
                    type="button"
                    onClick={() => onEditRollingLink(item.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-teal px-3 py-1.5 text-xs font-bold text-white transition hover:brightness-110"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                ) : (
                  /* The one way back into the frozen link's slot grid while this view is open. */
                  <button
                    type="button"
                    onClick={() => onOpenAdhocDetail(item.id)}
                    className={actionCls}
                  >
                    <CalendarDays className="h-3.5 w-3.5" /> Slots
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onCopy(item.path)}
                  className={actionCls}
                >
                  <Copy className="h-3.5 w-3.5" /> Copy link
                </button>
                <a
                  href={item.path}
                  target="_blank"
                  rel="noreferrer"
                  className={actionCls}
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </a>
                {/* Rolling links only: they are the long-lived, memorable ones worth putting on
                    a website. Embed mode works on all three kinds, so the others are a button away. */}
                {item.kind === "rolling" && (
                  <button
                    type="button"
                    onClick={() => setShowEmbed((v) => !v)}
                    className={cn(actionCls, showEmbed && "border-brand-blue text-brand-blue")}
                  >
                    <Code2 className="h-3.5 w-3.5" /> Embed
                  </button>
                )}
                {/* Lifecycle, moved here from the old gutter cards: this list is the only place
                    a rolling link can be switched off or removed. */}
                <button
                  type="button"
                  onClick={() => onToggleStatus(item.kind, item.id)}
                  className={actionCls}
                >
                  <Power className="h-3.5 w-3.5" /> {item.disabled ? "Enable" : "Disable"}
                </button>
                {item.kind === "poll" ? (
                  <button
                    type="button"
                    onClick={() => onRemovePoll?.(item.id)}
                    className={cn(actionCls, "hover:border-brand-coral hover:text-brand-coral")}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                ) : item.kind === "rolling" ? (
                  <button
                    type="button"
                    onClick={() => onRemoveRollingLink(item.id)}
                    className={cn(actionCls, "hover:border-red-400 hover:text-red-700")}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                ) : item.ended ? (
                  <button
                    type="button"
                    onClick={() => onArchive(item.id)}
                    className={cn(actionCls, "hover:border-red-400 hover:text-red-700")}
                  >
                    <Archive className="h-3.5 w-3.5" /> Archive
                  </button>
                ) : (
                  <button type="button" onClick={() => onExpire(item.id)} className={actionCls}>
                    <CalendarX2 className="h-3.5 w-3.5" /> Expire
                  </button>
                )}
              </div>
              {item.kind === "rolling" && showEmbed && (
                // The stored path is relative; the snippet has to carry an absolute URL because it
                // is pasted into somebody else's site. Same join the Copy link action makes.
                <EmbedSnippetPanel url={`${window.location.origin}${item.path}`} />
              )}
            </>
          )}

          {tab === "bookings" && (
            <section className="rounded-xl border border-border bg-background/60 p-4">
              {pending ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : activity?.error ? (
                <p className="text-xs text-muted-foreground">Couldn't load</p>
              ) : rows.length === 0 ? (
                <p className="text-xs italic text-muted-foreground">No bookings yet.</p>
              ) : (
                <>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {`${rows.length} booked`}
                    {cancelled > 0 ? ` · ${cancelled} cancelled` : ""}
                  </p>
                  <div className="max-h-72 overflow-y-auto">
                    <BookingRows rows={capped} />
                  </div>
                  {more && (
                    <button
                      type="button"
                      disabled={loadingMore}
                      onClick={() => {
                        // First press reveals what is already here; after that it fetches.
                        if (!showAll) setShowAll(true);
                        else onLoadMore(item.kind, item.id);
                      }}
                      className="mt-2 text-xs font-bold text-brand-blue hover:underline disabled:opacity-60"
                    >
                      {loadingMore
                        ? "Loading..."
                        : showAll
                          ? "Load more"
                          : `Show all (${total}) \u2192`}
                    </button>
                  )}
                </>
              )}
            </section>
          )}

          {tab === "history" &&
            (journalLocked ? <AuditLogUpsell /> : <EventTrail entries={entries} loading={pending} />)}
        </div>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold transition",
        active ? "bg-brand-blue text-white" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
